// Page chrome for /me/rate: the 72px dark bar with the (inverted) Kick logo
// and an optional right-hand action pill, a centered column, and an optional
// full-bleed lower band (the thank-you page's "What's next?" section).
// Rendered standalone by portalLayout — no PortalHeader, no nav drawer, no
// mainContent margin.
//
// Thank-you page geometry (S11): a wider column, the heading carrying its own
// top margin (topGap 0), and the review card straddling the band by
// `bandOverlap` px — main gets a negative bottom margin and the band grows to
// absorb leftover viewport height instead of main.

import { makeStyles } from "@mui/styles";

import {
  RATING_COLORS,
  RATING_COLUMN,
  RATING_FONT_FAMILY,
  RATING_MOBILE_MEDIA_QUERY,
} from "~/shared/styles/ratingPalette";
import logo from "~assets/images/logo.svg";

const HEADER_HEIGHT = 72;
const COLUMN_SIDE_PADDING = 16;
const BAND_TOP_PADDING = 48;

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    overflowX: "clip",
    fontFamily: RATING_FONT_FAMILY,
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.5,
    color: RATING_COLORS.textPrimary,
    WebkitFontSmoothing: "antialiased",
    "& *, & *::before, & *::after": {
      boxSizing: "border-box",
    },
  },
  header: {
    flexShrink: 0,
    height: HEADER_HEIGHT,
    backgroundColor: RATING_COLORS.headerBar,
    display: "flex",
    alignItems: "center",
  },
  headerInner: {
    width: "100%",
    maxWidth: "1184px",
    margin: "0 auto",
    padding: "0 32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [RATING_MOBILE_MEDIA_QUERY]: {
      padding: "0 16px",
    },
  },
  logoLink: {
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 0,
    borderRadius: "4px",
    "&:focus-visible": {
      outline: "2px solid #FFFFFF",
      outlineOffset: "4px",
    },
  },
  // There is no white logo asset; invert the navy one onto the dark bar.
  logo: {
    display: "block",
    height: "32px",
    width: "auto",
    filter: "brightness(0) invert(1)",
  },
  // Trustpilot's mobile header is the wordmark alone.
  headerAction: {
    [RATING_MOBILE_MEDIA_QUERY]: {
      display: "none",
    },
  },
  main: {
    flex: "1 0 auto",
    width: "100%",
    margin: "0 auto",
    padding: `0 ${COLUMN_SIDE_PADDING}px 48px`,
    marginTop: "var(--rating-top-gap)",
    [RATING_MOBILE_MEDIA_QUERY]: {
      marginTop: "var(--rating-top-gap-mobile)",
    },
  },
  mainOverlap: {
    flexGrow: 0,
    paddingBottom: 0,
    position: "relative",
    zIndex: 1,
  },
  band: {
    flexShrink: 0,
    backgroundColor: RATING_COLORS.thanksLowerBand,
    borderTop: `1px solid ${RATING_COLORS.borderCard}`,
  },
  bandGrow: {
    flexGrow: 1,
  },
  bandInner: {
    width: "100%",
    margin: "0 auto",
    padding: `${BAND_TOP_PADDING}px ${COLUMN_SIDE_PADDING}px 48px`,
  },
});

export default function RatingShell({
  background = RATING_COLORS.pageBg,
  band = null,
  bandOverlap = 0,
  columnWidth = RATING_COLUMN,
  topGap = 24,
  headerAction = null,
  children,
}) {
  const classes = useStyles();
  const columnMaxWidth = columnWidth + 2 * COLUMN_SIDE_PADDING;
  const overlap = Math.max(0, bandOverlap);

  return (
    <div
      className={classes.root}
      style={{
        backgroundColor: background,
        "--rating-top-gap": `${topGap}px`,
        "--rating-top-gap-mobile": `${Math.min(topGap, 16)}px`,
      }}
    >
      <header className={classes.header}>
        <div className={classes.headerInner}>
          <a
            href="https://www.gokick.com"
            className={classes.logoLink}
            aria-label="Kick"
          >
            <img src={logo} alt="" className={classes.logo} />
          </a>
          {headerAction ? (
            <div className={classes.headerAction}>{headerAction}</div>
          ) : null}
        </div>
      </header>
      <main
        className={[classes.main, overlap > 0 && classes.mainOverlap]
          .filter(Boolean)
          .join(" ")}
        style={{
          maxWidth: columnMaxWidth,
          marginBottom: overlap > 0 ? -overlap : undefined,
        }}
      >
        {children}
      </main>
      {band ? (
        <section
          className={[classes.band, overlap > 0 && classes.bandGrow]
            .filter(Boolean)
            .join(" ")}
        >
          <div
            className={classes.bandInner}
            style={{
              maxWidth: columnMaxWidth,
              paddingTop: overlap + BAND_TOP_PADDING,
            }}
          >
            {band}
          </div>
        </section>
      ) : null}
    </div>
  );
}
